// Konsern-/eierskapsmodell for selskapsinfo.
//
// Ren datamodell + rene selektorer (ingen React, ingen fetch). Bygger en
// konsern-graf av N selskaper med rettede kontroll-kanter (mor/datter) og
// reelle rettighetshavere (UBO) per selskap, utledet fra Brreg-roller/UBO.
//
// Input-formen (`KonsernKilde`) er et strukturelt undersett av `BrregResponse`
// (SPEC §2), så workerens flatede Brreg-svar kan sendes rett inn — uten import-
// kobling mot api-agentens `types.ts`. Integratoren kan sende `BrregResponse`
// direkte (TypeScript strukturell typing dekker det).

// ── Input fra worker (undersett av BrregResponse §2) ──────────────────────────
export interface KonsernRolleKilde {
  type: string; // rolle-beskrivelse, f.eks. "Styreleder"
  navn: string;
  orgnummer: string | null; // satt hvis rolleinnehaver er et selskap
  fratraadt: boolean;
}

export interface KonsernUbo {
  navn: string;
  fodselsaar: number | null;
  kontrollandel: string | null; // f.eks. "25-50 %"
}

export interface KonsernKilde {
  enhet: { organisasjonsnummer: string; navn: string } | null;
  roller: KonsernRolleKilde[];
  ubo: KonsernUbo[];
  uboAvailable: boolean;
}

// ── Modell ────────────────────────────────────────────────────────────────────
export type KonsernRelasjon = "aktiv" | "mor" | "datter";

export interface KonsernSelskap {
  orgNumber: string;
  name: string | null;
}

/** Rettet kontroll-kant: `kontrollor` (et selskap) har en rolle i `selskap`. */
export interface KonsernKant {
  kontrollor: string; // orgnr på selskapet som innehar rollen
  selskap: string; // orgnr på selskapet rollen gjelder
  rolle: string; // rolle-beskrivelse
}

export interface KonsernModell {
  selskaper: Record<string, KonsernSelskap>; // alle kjente selskaper, by orgnr
  kanter: KonsernKant[];
  ubo: Record<string, { eiere: KonsernUbo[]; tilgjengelig: boolean }>; // by orgnr
}

/** En node klar for visning, relativt til et aktivt selskap. */
export interface KonsernNode extends KonsernSelskap {
  relasjon: KonsernRelasjon;
  rolle: string | null; // rollen som binder noden til det aktive selskapet
}

export function tomKonsern(): KonsernModell {
  return { selskaper: {}, kanter: [], ubo: {} };
}

const norm = (orgnr: string) => orgnr.replace(/\s/g, "");

/**
 * Slår sammen ett selskaps Brreg-data inn i modellen (immutabelt → ny modell).
 * Registrerer selskapet selv, hvert rolleinnehaver-selskap som en kontroll-kant
 * (mor-retning), og UBO-listen for selskapet.
 */
export function leggTilSelskap(
  model: KonsernModell,
  orgnr: string,
  kilde: KonsernKilde
): KonsernModell {
  const org = norm(orgnr);
  const selskaper = { ...model.selskaper };
  selskaper[org] = {
    orgNumber: org,
    name: kilde.enhet?.navn ?? model.selskaper[org]?.name ?? null,
  };

  const kanter = [...model.kanter];
  for (const r of kilde.roller) {
    if (r.fratraadt || !r.orgnummer) continue;
    const mor = norm(r.orgnummer);
    if (mor === org) continue;
    if (!selskaper[mor]) selskaper[mor] = { orgNumber: mor, name: r.navn || null };
    const finnes = kanter.some(
      (k) => k.kontrollor === mor && k.selskap === org && k.rolle === r.type
    );
    if (!finnes) kanter.push({ kontrollor: mor, selskap: org, rolle: r.type });
  }

  return {
    selskaper,
    kanter,
    ubo: { ...model.ubo, [org]: { eiere: kilde.ubo, tilgjengelig: kilde.uboAvailable } },
  };
}

/**
 * Noder for visning relativt til `aktivOrg`: det aktive selskapet, dets mødre
 * (selskaper som innehar rolle i det) og dets døtre (selskaper det innehar rolle i).
 */
export function konsernNoder(model: KonsernModell, aktivOrg: string): KonsernNode[] {
  const org = norm(aktivOrg);
  const nodes: KonsernNode[] = [];
  const sett = new Set<string>();

  const aktiv = model.selskaper[org];
  nodes.push({
    orgNumber: org,
    name: aktiv?.name ?? null,
    relasjon: "aktiv",
    rolle: null,
  });
  sett.add(org);

  for (const k of model.kanter) {
    if (k.selskap === org && !sett.has(k.kontrollor)) {
      nodes.push({
        orgNumber: k.kontrollor,
        name: model.selskaper[k.kontrollor]?.name ?? null,
        relasjon: "mor",
        rolle: k.rolle,
      });
      sett.add(k.kontrollor);
    }
  }
  for (const k of model.kanter) {
    if (k.kontrollor === org && !sett.has(k.selskap)) {
      nodes.push({
        orgNumber: k.selskap,
        name: model.selskaper[k.selskap]?.name ?? null,
        relasjon: "datter",
        rolle: k.rolle,
      });
      sett.add(k.selskap);
    }
  }
  return nodes;
}

export function uboFor(
  model: KonsernModell,
  orgnr: string
): { eiere: KonsernUbo[]; tilgjengelig: boolean } {
  return model.ubo[norm(orgnr)] ?? { eiere: [], tilgjengelig: false };
}
